import { create } from 'zustand';

const LOCALE_STORAGE_KEY = 'fyd:locale';

function loadInitialLocale(): 'en' | 'zh' {
  try {
    const stored = typeof window !== 'undefined'
      ? window.localStorage.getItem(LOCALE_STORAGE_KEY)
      : null;
    if (stored === 'en' || stored === 'zh') return stored;
  } catch {
    // localStorage may be unavailable (SSR, privacy mode); fall through
  }
  return 'zh';
}

function persistLocale(locale: 'en' | 'zh') {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    }
  } catch {
    // best-effort persistence
  }
}

export type ModalID =
  | 'wallet_connect_modal'
  | 'payment_modal'
  | 'payment_failed_error_modal'
  | 'new_session_dialog'
  | 'iron_silent_recovery_banner'
  | 'persona_selection_modal'
  | 'session_confirmation_modal';

export interface ToastObject {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration: number;
}

interface UIStore {
  locale: 'en' | 'zh';
  toasts: ToastObject[];
  modal_stack: ModalID[];
  payment_tx_hash: string | null;
  setLocale: (locale: 'en' | 'zh') => void;
  pushToast: (message: string, type: ToastObject['type']) => void;
  popToast: (id: string) => void;
  pushModal: (modalId: ModalID) => void;
  popModal: () => void;
  clearModals: () => void;
  setPaymentTxHash: (txHash: string | null) => void;
}

export const useUIStore = create<UIStore>((set) => ({
  locale: loadInitialLocale(),
  toasts: [],
  modal_stack: [],
  payment_tx_hash: null,

  setLocale: (locale) => {
    persistLocale(locale);
    set({ locale });
  },
  pushToast: (message, type) =>
    set((s) => ({
      toasts: [
        ...s.toasts,
        { id: `toast_${Date.now()}_${s.toasts.length}`, message, type, duration: 4000 },
      ],
    })),
  popToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  pushModal: (modalId) =>
    set((s) => ({
      modal_stack: s.modal_stack[s.modal_stack.length - 1] === modalId
        ? s.modal_stack
        : [...s.modal_stack, modalId],
    })),
  popModal: () => set((s) => ({ modal_stack: s.modal_stack.slice(0, -1) })),
  clearModals: () => set({ modal_stack: [] }),
  setPaymentTxHash: (txHash) => set({ payment_tx_hash: txHash }),
}));

export const selectTopModal = (s: UIStore) => s.modal_stack[s.modal_stack.length - 1] ?? null;
